import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Edit2, Trash2, MessageSquarePlus, AlertTriangle } from 'lucide-react';
import { STATUS_META, STATUS_ORDER, PRIORITY_META, subtaskStats } from '@/constants/projectMeta';

const TH = ({ children, w, align = 'left' }) => (
  <th className={`text-${align} py-3 px-3 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider whitespace-nowrap ${w}`}>
    {children}
  </th>
);

export const ProjectTableView = ({ tasks, canManage, meId, onEdit, onDelete, onStatusChange, onAddUpdate, today }) => {
  if (!tasks.length) {
    return (
      <div className="py-16 text-center text-sm text-[hsl(var(--text-muted))]" data-testid="project-table-empty">
        No tasks yet. Click New Task to get started.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto" data-testid="project-table">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
            <TH w="w-12">No</TH>
            <th className="text-left py-3 px-4 text-[hsl(var(--text-muted))] font-semibold text-xs uppercase tracking-wider whitespace-nowrap min-w-[230px] sticky left-0 bg-[hsl(var(--surface))] z-10">
              Main Task
            </th>
            <TH w="min-w-[150px]">Project / Event</TH>
            <TH w="min-w-[140px]">Status</TH>
            <TH w="min-w-[150px]">Department</TH>
            <TH w="min-w-[120px]">PIC</TH>
            <TH w="min-w-[130px]">Collab</TH>
            <TH w="min-w-[110px]">Date Added</TH>
            <TH w="min-w-[110px]">Deadline</TH>
            <TH w="min-w-[130px]">Progress</TH>
            <TH w="min-w-[100px]">Priority</TH>
            <TH w="min-w-[170px]">Last Update</TH>
            <TH w="min-w-[110px]" align="right">Actions</TH>
          </tr>
        </thead>
        <tbody>
          {tasks.map((t) => {
            const meta = STATUS_META[t.status] || STATUS_META.pending;
            const overdue = t.deadline && t.deadline < today && !['completed', 'cancelled'].includes(t.status);
            const isMine = t.pic_id === meId || (t.collaborators || []).some((c) => c.id === meId);
            const lastUpdate = (t.updates || [])[(t.updates || []).length - 1];
            const collab = (t.collaborators || []).map((c) => c.name).join(', ');
            return (
              <tr key={t.id} className="border-b border-[hsl(var(--border))] hover:bg-white/[0.03] transition-colors group" data-testid={`project-row-${t.id}`}>
                <td className="py-3 px-3 text-[hsl(var(--text-muted))] text-xs whitespace-nowrap" data-testid={`project-no-${t.id}`}>{t.project_seq ?? t.seq}</td>
                <td className="py-3 px-4 whitespace-nowrap sticky left-0 bg-[hsl(var(--surface))] group-hover:bg-[hsl(var(--surface-elevated))] z-10">
                  <button className="max-w-[260px] truncate font-medium text-[hsl(var(--text-primary))] hover:text-[hsl(var(--primary))] transition-colors align-middle"
                    onClick={() => onAddUpdate(t)} title={`${t.title} — open details`} data-testid={`project-title-${t.id}`}>
                    {t.title}
                  </button>
                  {!!(t.subtasks || []).length && (
                    <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[hsl(var(--text-muted))] align-middle"
                      title="Subtasks done (cancelled not counted)" data-testid={`project-subtask-count-${t.id}`}>
                      {subtaskStats(t.subtasks).done}/{subtaskStats(t.subtasks).active}
                    </span>
                  )}
                </td>
                <td className="py-3 px-3 whitespace-nowrap" data-testid={`project-label-${t.id}`}>
                  {t.project
                    ? <span className="inline-block max-w-[140px] truncate px-1.5 py-0.5 rounded bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] text-[11px] align-middle" title={t.project}>{t.project}</span>
                    : <span className="text-[hsl(var(--text-muted))]">—</span>}
                </td>
                <td className="py-3 px-3 whitespace-nowrap">
                  {canManage || isMine ? (
                    <Select value={t.status} onValueChange={(v) => onStatusChange(t, v)}>
                      <SelectTrigger className={`h-7 w-[124px] text-xs ${meta.bg} ${meta.text} ${meta.border}`} data-testid={`project-status-${t.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={`${meta.bg} ${meta.text} ${meta.border}`}>{meta.label}</Badge>
                  )}
                </td>
                <td className="py-3 px-3 text-xs text-[hsl(var(--text-muted))] whitespace-nowrap">{t.department || '—'}</td>
                <td className="py-3 px-3 text-xs whitespace-nowrap">
                  <span className="inline-block max-w-[110px] truncate align-middle" title={t.pic_name || ''}>{t.pic_name || '—'}</span>
                </td>
                <td className="py-3 px-3 text-[11px] text-[hsl(var(--text-muted))] whitespace-nowrap">
                  <span className="inline-block max-w-[120px] truncate align-middle" title={collab}>{collab || '—'}</span>
                </td>
                <td className="py-3 px-3 text-xs text-[hsl(var(--text-muted))] whitespace-nowrap" data-testid={`project-date-added-${t.id}`}>
                  {t.date_added || '—'}
                </td>
                <td className="py-3 px-3 text-xs whitespace-nowrap">
                  {t.deadline ? (
                    <span className={overdue ? 'text-red-400 font-medium' : ''} data-testid={`project-deadline-${t.id}`}>
                      {t.deadline}{overdue && <AlertTriangle size={11} className="inline ml-1" />}
                    </span>
                  ) : '—'}
                </td>
                <td className="py-3 px-3 whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    <div className="w-[72px] h-1.5 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${t.progress || 0}%`, background: meta.accent }} />
                    </div>
                    <span className="text-[11px] text-[hsl(var(--text-muted))] w-8 shrink-0">{t.progress || 0}%</span>
                  </div>
                </td>
                <td className="py-3 px-3 text-xs whitespace-nowrap">
                  <span className={(PRIORITY_META[t.priority] || PRIORITY_META.medium).text}>
                    {(PRIORITY_META[t.priority] || PRIORITY_META.medium).label}
                  </span>
                </td>
                <td className="py-3 px-3 text-[11px] text-[hsl(var(--text-muted))] whitespace-nowrap">
                  {lastUpdate ? `${lastUpdate.week} · ${lastUpdate.by_name}` : 'No update yet'}
                </td>
                <td className="py-3 px-3 whitespace-nowrap">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onAddUpdate(t)} title="Updates & comments"
                      className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))] hover:text-[hsl(var(--primary))] relative"
                      data-testid={`project-update-btn-${t.id}`}>
                      <MessageSquarePlus size={14} />
                      {!!(t.comments || []).length && (
                        <span className="absolute -top-0.5 -right-0.5 text-[9px] px-1 rounded-full bg-[hsl(var(--primary))] text-white"
                          data-testid={`project-comment-count-${t.id}`}>
                          {(t.comments || []).length}
                        </span>
                      )}
                    </button>
                    {(canManage || isMine) && (
                      <button onClick={() => onEdit(t)} title="Edit"
                        className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))] hover:text-blue-400"
                        data-testid={`project-edit-btn-${t.id}`}>
                        <Edit2 size={14} />
                      </button>
                    )}
                    {canManage && (
                      <button onClick={() => onDelete(t)} title="Delete"
                        className="p-1.5 rounded hover:bg-white/10 text-[hsl(var(--text-muted))] hover:text-red-400"
                        data-testid={`project-delete-btn-${t.id}`}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
